/**
 * web/src/components/gates/RejectModal.tsx
 * Modal with required reason textarea. Server enforces non-empty via Zod
 * (RejectBody.reason min(1)); client disables submit while reason is empty
 * (matches prototype line 679 `disabled={!rejectReason.trim()}`).
 */
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../ui/dialog';
import { Textarea } from '../shared/Textarea';
import { Button } from '../shared/Button';

/** Public props for RejectModal. */
export interface RejectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the trimmed, non-empty reason. */
  onConfirm: (reason: string) => void;
  /** When true, disables the Reject button (e.g., while the mutation is pending). */
  isSubmitting?: boolean;
}

/**
 * Reject gate confirmation modal. Reason is required; whitespace-only
 * input is rejected at the client. Resets reason state on close so the
 * next open starts fresh.
 *
 * @sideEffects holds local reason state; clears on close
 * @idempotent yes — repeated opens don't leak state
 */
export function RejectModal({ open, onOpenChange, onConfirm, isSubmitting }: RejectModalProps) {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();

  // Reset on close so the next open starts blank.
  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject this gate?</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="reject-reason" className="text-[12px] font-medium text-text-muted">
              Reason for rejection{' '}
              <span className="text-failed" aria-hidden="true">
                *
              </span>
            </label>
            <Textarea
              id="reject-reason"
              rows={4}
              placeholder="Tell the upstream reviewer what needs to change…"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoFocus
            />
            <span className="text-[11px] text-text-subtle">
              The workflow stays paused and the gate returns to the queue for
              edit &amp; resubmit. The dashboard updates in real time.
            </span>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="default" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            disabled={!trimmed || isSubmitting}
            onClick={() => onConfirm(trimmed)}
          >
            Reject &amp; send back
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
